import { PaymentService, PaymentSettings } from "@scopely/web-sdk-payments";
import { requestApi } from "~/api/requestApi";
import { TITAN_API_KEY } from "~/constants/providers";
import { tracker } from "../scopely/scopelyId";

interface TCheckoutRequest {
  sku: string;
  userId: string;
  deviceToken: string;
  trackingId: string;
  metadata: {
    DisplayName: string;
  };
  locale: string;
  quantity: number;
}

interface TFetchParams {
  endPoint: string;
  method: "get" | "post" | "put" | "delete";
  params?: Record<string, unknown>;
  body?: Record<string, unknown>;
}

const getSessionToken = () => {
  return localStorage.getItem("sessionToken");
};
const authStorage = localStorage.getItem("auth-storage");
const userId = (authStorage && JSON.parse(authStorage)?.state?.user?.Id) || "";

const GAME_API_KEY = TITAN_API_KEY;
const PENDING_PAYMENTS_INTERVAL_MS = 180_000;

const settings = new PaymentSettings(GAME_API_KEY, "Wheel of Fortune");

const COMMON_PARAMS = {
  bypassSignature: false,
  c_bundle: "WheelOfFortune",
  c_platform: "web",
  c_store: "Playgami",
};

class GameMakerApiClient {
  async sendCreateCheckoutRequest(sessionToken: string | null, checkoutRequest: TCheckoutRequest) {
    try {
      const responseBody = await this.sendRequest({
        endPoint: "playgami-payments/payment",
        body: checkoutRequest,
        method: "post",
        params: {
          ...COMMON_PARAMS,
          sessionToken: sessionToken,
          c_titan_token: tracker.getDeviceToken()
        },
      });

      return {
        paymentId: responseBody.PaymentId,
        provider: responseBody.Provider,
      };
    } catch {
      throw new Error("Failed to create a checkout");
    }
  }

  async sendClaimPaymentRequest(paymentId: string, sessionToken: string | null) {
    try {
      return await this.sendRequest({
        endPoint: "playgami-payments/payment/claim",
        method: "post",
        params: {
          ...COMMON_PARAMS,
          SessionToken: sessionToken,
          c_titan_token: tracker.getDeviceToken()
        },
        body: {
          paymentId: paymentId,
        },
      });
    } catch {
      throw new Error("Failed to claim payment");
    }
  }

  async sendGetUnclaimedPaymentsRequest(sessionToken: string | null) {
    try {
      return await this.sendRequest({
        endPoint: "playgami-payments/payment/unclaimed",
        method: "get",
        params: {
          ...COMMON_PARAMS,
          SessionToken: sessionToken,
          c_titan_token: tracker.getDeviceToken()
        },
      });
    } catch {
      throw new Error("Failed to list unclaimed payments");
    }
  }

  async sendRequest(fetchParams: TFetchParams) {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const response: any = await requestApi({
      endpoint: fetchParams.endPoint,
      method: fetchParams.method,
      params: fetchParams.params,
      body: fetchParams.body,
      useDefaultQueryParams: false,
    });

    // if (!response?.data) {
    //     throw new Error('Invalid backend response: ' + response);
    // }
    // if (!response.data.IsSuccess) {
    //     throw new Error('Request to the server was not successful: ' + response.Error);
    // }

    return response?.Data;
  }
}

class GameMakerGameDelegate {
  client: GameMakerApiClient;
  currentUserId: string = userId;
  interval: NodeJS.Timeout | null = null;

  constructor() {
    this.client = new GameMakerApiClient();
  }
  setCurrentUserId = (userId: string) => {
    if (this.currentUserId === userId) {
      return;
    }
    if (this.currentUserId && this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.currentUserId = userId;
    if (this.currentUserId != null) {
      setTimeout(this.claimPending, 1_000);
    }
  };
  createCheckout = async (checkoutRequest: TCheckoutRequest) => {
    if (!this.currentUserId) {
      throw new Error("No user logged in. Cannot start a payment");
    }
    if (!getSessionToken()) {
      throw new Error("No session token available. Cannot start a payment");
    }

    if (!this.interval) {
      this.interval = setInterval(this.claimPending, PENDING_PAYMENTS_INTERVAL_MS);
    }

    return await this.client.sendCreateCheckoutRequest(getSessionToken(), {
      ...checkoutRequest,
      userId: this.currentUserId,
    });
  };
  claimPayment = async (paymentId: string) => {
    if (!this.currentUserId) {
      throw new Error("No user logged in. Cannot claim a payment");
    }
    if (!getSessionToken()) {
      throw new Error("No session token available. Cannot claim a payment");
    }

    return await this.client.sendClaimPaymentRequest(paymentId, getSessionToken());
  };
  claimPending = async () => {
    if (!(this.currentUserId && getSessionToken())) {
      return;
    }

    const pending = await this.client.sendGetUnclaimedPaymentsRequest(getSessionToken());

    for (const paymentId of pending.PendingPayments) {
      this.claimPayment(paymentId);
    }
  };
}

const paymentsBackendDelegate = new GameMakerGameDelegate();

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const playgamiPayments = new PaymentService(settings, paymentsBackendDelegate, tracker as any);

export { paymentsBackendDelegate, playgamiPayments };
